import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ButtonSpinner } from '../../components/ButtonSpinner'
import { PageScrollLayout, PageTableStack } from '../../components/PageScrollLayout'
import { PsRatingAdjustTable } from '../../components/admin/PsRatingAdjustTable'
import { PsRatingLeagueBalance } from '../../components/admin/PsRatingLeagueBalance'
import {
  Api,
  ApiError,
  type PsRatingAdjustmentInput,
  type RunInfo,
} from '../../services/api'
import {
  applySingleTeamAdjustment,
  effectiveMapFromRows,
  PS_RATING_MAX,
  PS_RATING_MIN,
  PS_RATING_STEP,
  roundRating,
} from '../../utils/psRatingZeroSum'

export function PreSeasonRatingsAdjustPage() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Record<string, number>>({})
  const [activeTeam, setActiveTeam] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [runInfo, setRunInfo] = useState<RunInfo | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const ratingsQuery = useQuery({
    queryKey: ['ps-ratings'],
    queryFn: Api.psRatings,
  })

  useEffect(() => {
    if (!ratingsQuery.data?.rows) return
    setDraft((prev) => {
      if (Object.keys(prev).length) return prev
      return effectiveMapFromRows(ratingsQuery.data.rows)
    })
  }, [ratingsQuery.data])

  const rows = useMemo(
    () => [...(ratingsQuery.data?.rows ?? [])].sort((a, b) => a.team.localeCompare(b.team)),
    [ratingsQuery.data?.rows],
  )

  const hasDraft = activeTeam != null

  const draftRatings = useMemo(() => {
    if (Object.keys(draft).length > 0) return draft
    return effectiveMapFromRows(rows)
  }, [draft, rows])

  const changes = useMemo<PsRatingAdjustmentInput[]>(() => {
    if (!activeTeam) return []
    const row = rows.find((r) => r.team === activeTeam)
    if (!row) return []
    const next = draft[activeTeam]
    if (next == null || Math.abs(next - row.effective_rating) < 0.0005) return []
    return [{ team: activeTeam, new_rating: roundRating(next), reason: reason.trim() }]
  }, [activeTeam, draft, reason, rows])

  const saveMutation = useMutation({
    mutationFn: (body: PsRatingAdjustmentInput[]) => Api.savePsRatingAdjustments(body),
    onSuccess: async (data) => {
      qc.setQueryData(['ps-ratings'], { rows: data.rows })
      await qc.invalidateQueries({ queryKey: ['ps-ratings'] })
      await qc.invalidateQueries({ queryKey: ['ps-ratings', 'audit'] })
      setDraft(effectiveMapFromRows(data.rows))
      setActiveTeam(null)
      setReason('')
      setSaveSuccess(true)
    },
  })

  const simulateMutation = useMutation({
    mutationFn: Api.triggerRun,
    onSuccess: (info) => {
      setRunInfo(info)
      void qc.invalidateQueries({ queryKey: ['runs', 'latest'] })
    },
  })

  const runStatusQuery = useQuery({
    queryKey: ['runs', runInfo?.run_id],
    queryFn: () => Api.getRun(runInfo!.run_id),
    enabled: !!runInfo?.run_id,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2500 : false),
  })

  useEffect(() => {
    const status = runStatusQuery.data?.status
    if (status === 'succeeded' || status === 'failed') {
      void qc.invalidateQueries({ queryKey: ['runs', 'latest'] })
      void qc.invalidateQueries({ queryKey: ['ratings'] })
      void qc.invalidateQueries({ queryKey: ['standings'] })
      void qc.invalidateQueries({ queryKey: ['ps-ratings'] })
    }
  }, [qc, runStatusQuery.data?.status])

  const saving = saveMutation.isPending || simulateMutation.isPending
  const simulatePhase = saveMutation.isSuccess && simulateMutation.isPending

  const save = async (simulate: boolean) => {
    setSaveSuccess(false)
    await saveMutation.mutateAsync(changes)
    if (simulate) {
      await simulateMutation.mutateAsync()
    }
  }

  const resetDraft = () => {
    setDraft(effectiveMapFromRows(rows))
    setActiveTeam(null)
    setReason('')
    setSaveSuccess(false)
  }

  const handleActivate = (team: string) => {
    setActiveTeam(team)
    setSaveSuccess(false)
  }

  const handleRatingChange = (team: string, value: number) => {
    const base = effectiveMapFromRows(rows)
    setActiveTeam(team)
    setDraft(applySingleTeamAdjustment(base, team, value))
    setSaveSuccess(false)
  }

  const err = ratingsQuery.error ?? saveMutation.error ?? simulateMutation.error
  const visibleRun = runStatusQuery.data ?? runInfo

  if (ratingsQuery.isLoading) {
    return <p className="text-slate-500">Loading PS ratings…</p>
  }

  const adjustLead = (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          <p className="font-medium">Pre-season ratings action failed.</p>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-rose-800">
            {err instanceof ApiError ? err.message : String(err)}
          </pre>
        </div>
      )}

      <div className="card space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="min-w-80 flex-1 space-y-1">
            <span className="text-sm font-medium text-slate-700">Reason for this adjustment</span>
            <input
              type="text"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                setSaveSuccess(false)
              }}
              placeholder="Prospect breakout, starter injury, role change..."
              disabled={saving}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {saveSuccess && !saving && (
              <span className="text-sm font-medium text-emerald-700">Saved</span>
            )}
            <button
              type="button"
              className="btn-secondary"
              disabled={!hasDraft || saving}
              onClick={resetDraft}
            >
              Reset unsaved
            </button>
            <SaveActionButton
              variant="secondary"
              label="Save"
              pendingLabel="Saving…"
              pending={saveMutation.isPending && !simulateMutation.isPending}
              disabled={!changes.length || saving}
              onClick={() => void save(false)}
            />
            <SaveActionButton
              variant="primary"
              label="Save and simulate"
              pendingLabel={simulatePhase ? 'Starting simulation…' : 'Saving…'}
              pending={saving}
              disabled={!changes.length || saving}
              onClick={() => void save(true)}
            />
          </div>
        </div>
        <p className="text-xs text-slate-500">
          {hasDraft
            ? `Editing ${activeTeam}. Other sliders are locked and show zero-sum offsets.`
            : `Select a team to adjust. Slider range is ${PS_RATING_MIN} to ${PS_RATING_MAX} (step ${PS_RATING_STEP}).`}
        </p>
      </div>

      <PsRatingLeagueBalance
        ratings={draftRatings}
        serverOk={ratingsQuery.data?.balance?.ok}
      />

      {visibleRun && (
        <SimulationStatus
          info={visibleRun}
          loading={simulateMutation.isPending || runStatusQuery.isFetching}
        />
      )}
    </div>
  )

  return (
    <PageScrollLayout
      growTable
      title="Adjust Pre-Season Ratings"
      subtitle={
        <>
          Move one team at a time. The other 29 ratings rebalance equally so the league average stays at zero. The
          original CSV remains the baseline; every save is written to the audit log.
        </>
      }
      lead={adjustLead}
    >
      <PageTableStack grow>
        <div
          className={[
            'flex min-h-0 min-w-0 flex-1 flex-col',
            saving ? 'pointer-events-none opacity-60' : '',
          ].join(' ')}
        >
          <PsRatingAdjustTable
            rows={rows}
            draft={draft}
            activeTeam={activeTeam}
            hasDraft={hasDraft}
            saving={saving}
            embedded={false}
            fillViewport
            onActivate={handleActivate}
            onChange={handleRatingChange}
          />
        </div>
      </PageTableStack>
    </PageScrollLayout>
  )
}

function SaveActionButton({
  variant,
  label,
  pendingLabel,
  pending,
  disabled,
  onClick,
}: {
  variant: 'primary' | 'secondary'
  label: string
  pendingLabel: string
  pending: boolean
  disabled: boolean
  onClick: () => void
}) {
  const base = variant === 'primary' ? 'btn-primary' : 'btn-secondary'
  return (
    <button
      type="button"
      className={[
        base,
        'gap-2',
        pending ? 'ring-2 ring-inset ring-slate-400' : '',
        pending && variant === 'primary' ? '!bg-slate-950' : '',
        pending && variant === 'secondary' ? '!bg-slate-100' : '',
      ].join(' ')}
      disabled={disabled}
      aria-busy={pending}
      onClick={onClick}
    >
      {pending && <ButtonSpinner />}
      {pending ? pendingLabel : label}
    </button>
  )
}

function SimulationStatus({ info, loading }: { info: RunInfo; loading: boolean }) {
  const running = info.status === 'running' || info.status === 'pending'
  const failed = info.status === 'failed'
  const succeeded = info.status === 'succeeded'
  return (
    <div
      className={[
        'rounded-lg border p-4 text-sm',
        failed
          ? 'border-rose-200 bg-rose-50 text-rose-900'
          : succeeded
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
            : 'border-blue-200 bg-blue-50 text-blue-900',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">
            {failed ? 'Simulation failed' : succeeded ? 'Simulation complete' : 'Simulation started'}
          </p>
          <p className="mt-1">
            {failed
              ? info.error || `Pipeline exited with code ${info.returncode ?? 'unknown'}.`
              : succeeded
                ? 'Ratings, standings, and matchups are refreshing with the new PS ratings.'
                : 'The season simulation is running now. This can take a few minutes.'}
          </p>
        </div>
        <span className="rounded bg-white/70 px-2 py-1 font-mono text-xs">
          {loading && running ? 'polling · ' : ''}
          {info.status}
        </span>
      </div>
      <p className="mt-2 break-all font-mono text-xs opacity-75">{info.run_id}</p>
    </div>
  )
}
